import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import EventEmitter from 'src/utils/EventEmitter';
import '../styles/upload_sales_popup.css';
import { UploadPopup } from '../components/UploadPopup';
import { useAxiosInstance } from 'src/utils/useAxiosInstance';
import { useMutation, useQuery } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

export const steps = {
  confirm: 0,
  loading: 1,
  uploading: 2,
  complete: 3,
  error: 4,
  errorFirst: 5
};

const pollingTypes = {
  upload: 'upload',
  submit: 'submit',
}

export default function UploadSalesPopup(props) {
  const { axiosInstance } = useAxiosInstance();
  const file = props.customData.fileName;
  const fileName = file.name;
  const fileType = file.type;
  const isWrongFileType = fileType.search('sheet') < 0;
  const [step, setStep] = useState(isWrongFileType ? steps.errorFirst : steps.confirm);
  
  const [pollingType, setPollingType] = useState(undefined);

  const uploadFileMutation = useMutation({
    mutationFn: async (file) => {
      const fileType = file.type;

      const {
        data: { url, data: { uploadId } },
      } = await axiosInstance.post(`/sales/upload?fileName=${file.name}`);

      await axios.put(url, file, {
        headers: { 'Content-Type': fileType },
      });

      return uploadId;
    },
    onSuccess: () => {
      setPollingType(pollingTypes.upload);
    }
  });

  // Reset mutation after successful upload completes (5 minute timeout)
  useEffect(() => {
    const RESET_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
    let timeoutId = null;

    if (pollingType) {
      timeoutId = setTimeout(() => {
        setPollingType(undefined); // Reset polling type
        enqueueSnackbar('Upload timed out. Please try again.', { variant: 'error' });
        onClosePopup();
      }, RESET_TIMEOUT);
    }

    // Cleanup function that runs on unmount or when dependency changes
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [pollingType]);

  const pollingQuery = useQuery({
    queryKey: ['polling', uploadFileMutation.data],
    queryFn: async () => {
      const uploadId = uploadFileMutation.data;
      const res = await axiosInstance.get(`/sales/upload/polling/${uploadId}`);
      return res.data;
    },
    staleTime: 0,
    retry: false,
    refetchInterval: pollingType ? 2000 : false,
    enabled: Boolean(pollingType),
  });

  // Stop polling when the upload is finished
  useEffect(() => {
    if (pollingType === pollingTypes.upload) {
      const isCompleted = pollingQuery.data?.data.status === 'completed';
      const message = pollingQuery.data?.data.message;
      
      if (!isCompleted) return;
      
      setPollingType(undefined);
      setStep(message ? steps.error : steps.uploading);
    }

    if (pollingType === pollingTypes.submit) {
      const isCompleted = pollingQuery.data?.data.statusSubmit === 'completed';
      
      if (!isCompleted) return;
      
      setPollingType(undefined);
      setStep(steps.complete);
    }
  }, [pollingQuery.data, pollingType]);

  const submitFileMutation = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.post(`/sales/upload/submit/${uploadFileMutation.data}?fileName=${fileName}`);
      return res.data
    },
    onSuccess: () => {
      setPollingType(pollingTypes.submit);
    },
    onError: () => {
      setStep(steps.uploading);
    }
  })

  const downloadErrorFile = useMutation({
    mutationFn: async () => {
      const res = await axiosInstance.get(
        `/consignment-transfer-order/get-download-file?documentType=error&documentName=${fileName}`,
      );
      return res.data;
    },
    onSuccess: (data) => {
      window.open(data.data.signedUrl, '_blank');
    },
  });

  const onClosePopup = () => {
    EventEmitter.emit('show_custom_popup', { value: false });
  };

  const uploadFileToValidate = async () => {
    setStep(steps.loading);

    uploadFileMutation.mutate(file);
  };

  const handleSubmitFile = async () => {
    setStep(steps.loading);
    submitFileMutation.mutate();
  };

  const handleAcknowledge = () => {
    setStep(steps.uploading);
  }

  return (
    <div className="custom-popup-container-wrapper" style={{ height: '100%' }}>
      <div className="custom-popup-container-wrapper-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          Cannatrek Consignment Hub
        </div>
        <div className="hide-mobile">
          {step !== steps.uploading && (
            <CloseIcon
              className="close-popup-icon"
              onClick={() => onClosePopup()}
            />
          )}
        </div>
      </div>
      <UploadPopup
        step={step}
        file={file}
        onClosePopup={onClosePopup}
        uploadFileToValidate={uploadFileToValidate}
        handleSubmitFile={handleSubmitFile}
        handleAcknowledge={handleAcknowledge}
        UploadResponse={pollingQuery.data?.data.message || []}
        downloadErrorFile={downloadErrorFile}
      />
    </div>
  );
}
